import { useEffect, useRef } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { scriptsCollection } from "@/lib/collections/scripts-collection";
import { httpStore } from "@/lib/api/instance";
import { getQueue } from "@/lib/queue-store";
import { StoreAuthException } from "@/lib/api/errors";
import { applyReviewState } from "../lib/apply-review-state";
import { setFlag } from "../lib/flag-message-store";
import { deleteScriptAndPages } from "./use-scripts";

const POLL_MS = 5000;

/** Mounted once above both the Home and script-detail screens. Every 5s
 * while the tab is visible, asks the server what it now thinks of each
 * script this device has already uploaded, and reconciles: approved (or
 * simply gone) wipes the local copy, flagged shows a banner, pending
 * clears one. A failed request is a no-op — never purge or clear a flag
 * on a guess. */
export function useReviewStatePoll(): void {
  const { data: scripts } = useLiveQuery({ query: q => q.from({ script: scriptsCollection }) });
  const remoteIds = scripts.map(s => s.remoteId).filter((id): id is string => !!id);
  const remoteIdsKey = remoteIds.join(",");

  /** Message text last vibrated for, per local script id — buzz once per
   * new unacked flag, not every 5s while it's still sitting there. */
  const notifiedRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!remoteIdsKey) return;
    const ids = remoteIdsKey.split(",");

    async function tick() {
      if (document.hidden) return;
      let rows: Awaited<ReturnType<typeof httpStore.reviewState>> = [];
      let httpOk = true;
      try {
        rows = await httpStore.reviewState(ids);
      } catch (e) {
        httpOk = false;
        if (e instanceof StoreAuthException) {
          getQueue()?.pause("auth");
        }
      }

      const { purgeRemoteIds, flagByRemoteId } = applyReviewState(ids, rows, httpOk);

      for (const remoteId of purgeRemoteIds) {
        const local = scriptsCollection.toArray.find(s => s.remoteId === remoteId);
        if (local) await deleteScriptAndPages(local.id);
      }
      for (const [remoteId, flag] of Object.entries(flagByRemoteId)) {
        const local = scriptsCollection.toArray.find(s => s.remoteId === remoteId);
        if (!local) continue;
        setFlag(local.id, flag);

        if (flag && !flag.ackedAt && flag.message) {
          if (notifiedRef.current.get(local.id) !== flag.message) {
            notifiedRef.current.set(local.id, flag.message);
            navigator.vibrate?.(200);
          }
        } else {
          notifiedRef.current.delete(local.id);
        }
      }
    }

    // Leading tick: don't leave the phone blind for the first 5s after
    // mount, an app foreground, or a screen unlock.
    void tick();
    const onVisible = () => {
      if (!document.hidden) void tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(() => void tick(), POLL_MS);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [remoteIdsKey]);
}
